import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col, notification, Upload } from "antd";
import { useTranslation } from "react-i18next";
import { EditOutlined } from "@ant-design/icons";
import AdminName from "../../workers/admin-details-form/AdminName";
import { USER_PROFILE } from "@src/helpers/imagesConstants";
import AdminUniqueId from "../../workers/admin-details-form/AdminUniqueId";
import AdminMobile from "../../workers/admin-details-form/AdminMobile";
import AdminEmail from "../../workers/admin-details-form/AdminEmail";
import AdminUserName from "../../workers/admin-details-form/AdminUsename";
import AdminPassword from "../../workers/admin-details-form/AdminPassword";
import ChangePasswordOnLogin from "../../workers/admin-details-form/ChangePasswordLogin";
import { retrieveItem } from "@src/helpers/functions";
import { useDispatch, useSelector } from "react-redux";
import {
  AppDispatch,
  createUserRole,
  createUserRoleClear,
  createUserRoleRequest,
  getMyOrganization,
  getUserRoleList,
  getUserRoleListRequest,
  updateUserRole,
  updateUserRoleClear,
  updateUserRoleRequest,
  useDynamicSelector,
  User,
} from "@src/redux";
import toast from "react-hot-toast";
import {
  action,
  handleBeforeUpload,
  handleFileChanged,
  handleUpload,
} from "@src/helpers/fileUpload";
import { MdEdit } from "react-icons/md";
import { lowerCase, startCase } from "lodash";
import DynamicTreeSelect from "../../common/TreeSelect";

interface AdminFormProps {
  mutationType: "edit" | "add";
  onCancel: Function;
  cardTitle?: string;
  roleType?: any;
  record?: User;
  setModalVisible?: any;
}

const RoleForm: React.FC<AdminFormProps> = ({
  mutationType,
  roleType,
  cardTitle,
  record,
  onCancel,
}) => {
  const [mobile, setMobile] = useState({});
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const profile = Form.useWatch("profile", form);
  const { filter } = useDynamicSelector("role_filter");

  const {
    result: createUserRoleResult,
    error: createUserRoleError,
    loading: createUserRoleLoading,
  } = useSelector(createUserRole);
  const {
    result: updateUserRoleResult,
    error: updateUserRoleError,
    loading: updateUserRoleLoading,
  } = useSelector(updateUserRole);

  const onPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const password = event.target.value;
    if (!password) {
      form.setFieldsValue({ changePasswordOnLogin: false });
    }
  };

  useEffect(() => {
    if (record) {
      setMobile({
        number: record?.mobile?.number,
        country_code: record?.mobile?.country_code,
        country_short_code: record?.mobile?.shortCode,
      });

      if (mutationType === "edit") {
        form.setFieldsValue({
          name: record?.name,
          code: record?.code,
          email: record?.email,
          mobile:
            record?.mobile?.number?.length > 10
              ? record?.mobile.number
              : String(record?.mobile?.country_short_code) +
                String(record?.mobile?.number),
          systemUser: {
            username: record?.systemUser?.username?.split("~")?.[1] || "",
          },
        });
      }
    }
  }, [mutationType, record, form]);

  const fieldLabels: Record<string, string> = {
    username: "username",
    email: "email address",
    code: "unique ID",
    name: "name",
    mobile: "mobile number",
  };

  const humanizeField = (rawField: string) => {
    const key = rawField.trim().toLowerCase();
    if (fieldLabels[key]) return fieldLabels[key];
    return rawField
      .trim()
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase();
  };

  const resolveErrorMessage = (message?: string) => {
    if (!message) return t("something_went_wrong");

    // API sends raw "Unique constraint error on field: [username]" – turn it
    // into a message the user can actually understand.
    const uniqueMatch = message.match(
      /unique constraint error on fields?:?\s*\[?([^\]]+)\]?/i
    );
    if (uniqueMatch) {
      const label = humanizeField(uniqueMatch[1]);
      return `This ${label} is already in use. Please use a different ${label}.`;
    }

    return message.toLowerCase().includes("prisma")
      ? t("something_went_wrong")
      : message;
  };

  useEffect(() => {
    if (createUserRoleResult?.status === "success") {
      toast.success(t("create_role_success"), { id: "create_user_role" });
      dispatch(createUserRoleClear());
      dispatch(
        getUserRoleListRequest({
          filter: { ...filter },
        })
      );
      onCancel();
    } else if (createUserRoleResult?.error?.message || createUserRoleError) {
      toast.error(
        resolveErrorMessage(
          createUserRoleResult?.error?.message || createUserRoleError?.message
        ),
        { id: "create_user_role" }
      );
      dispatch(createUserRoleClear());
    }
  }, [createUserRoleResult, createUserRoleError]);

  useEffect(() => {
    if (updateUserRoleResult?.status === "success") {
      toast.success(t("update_role_success"), { id: "update_user_role" });
      dispatch(updateUserRoleClear());
      dispatch(
        getUserRoleListRequest({
          filter: { ...filter },
        })
      );
      onCancel();
    } else if (updateUserRoleResult?.error?.message || updateUserRoleError) {
      toast.error(
        resolveErrorMessage(
          updateUserRoleResult?.error?.message || updateUserRoleError?.message
        ),
        { id: "update_user_role" }
      );
      dispatch(updateUserRoleClear());
    }
  }, [updateUserRoleResult, updateUserRoleError]);

  const handleChangeNumber = (values: {
    number: string;
    country_code: string;
    country_short_code: string;
  }) => {
    setMobile(values);
  };

  const onFinish = (values: any) => {
    const orgArr = Array.isArray(values?.organizationId)
      ? values.organizationId
      : [];
    const deepestSelected = [...orgArr]
      .reverse()
      .find((id: any) => typeof id === "string" && id.length > 0);
    values.organizationId = deepestSelected || values?.my_org;
    if (mutationType === "add") {
      dispatch(
        createUserRoleRequest({
          data: {
            name: values?.name,
            code: values?.code,
            photo: profile?.file?.url,
            email: values?.email,
            systemUser: { ...values?.systemUser },
            mobile: mobile,
            type: roleType,
            organizationId: values?.organizationId,
          },
        })
      );
    } else {
      dispatch(
        updateUserRoleRequest({
          id: record?.id || "",
          data: {
            name: values?.name,
            code: values?.code,
            email: values?.email,
            photo: profile?.file?.url,
            systemUser: {
              password: values?.systemUser?.password,
              forcePasswordChange:
                values?.systemUser?.forcePasswordChange || false,
            },
            mobile: mobile,
            organizationId: record?.organizationId,
          },
        })
      );
    }
  };

  return (
    <Form layout="vertical" form={form} onFinish={onFinish}>
      {mutationType === "add" && (
        <Row gutter={16}>
          <DynamicTreeSelect
            form={form}
            mutationType={"add"}
            isRequired={false}
            allowClear={false}
            className="text"
            key={"role_form"}
          />
        </Row>
      )}
      {/* {mutation_type === "edit" && ( */}
      <Row gutter={16} justify="center" align="middle">
        <Form.Item name={"profile"}>
          <Upload
            action={action}
            customRequest={(e) => handleUpload(e)}
            beforeUpload={(args) => handleBeforeUpload(args, "profile",['image/jpeg',"image/jpg","image/png"])}
            onChange={(e) => handleFileChanged(e)}
            showUploadList={false}
          >
            <div className="user-profile-wrapper">
              <img
                src={
                  profile?.fileList?.[0]?.url ||
                  profile?.file?.url ||
                  record?.photo ||
                  USER_PROFILE
                }
                className="user_profile_image"
                alt="User Profile"
              />

              {<MdEdit className="edit-icon" />}
            </div>
          </Upload>
        </Form.Item>
      </Row>
      {/* )} */}

      <Row gutter={16}>
        <Col span={12}>
          <AdminName />
        </Col>
        <Col span={12}>
          <AdminUniqueId />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <AdminMobile onChange={handleChangeNumber} />
        </Col>
        <Col span={12}>
          <AdminEmail />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <AdminUserName
            mutationType={mutationType}
            username={record?.systemUser?.username}
          />
        </Col>
        <Col span={12}>
          <AdminPassword
            onPasswordChange={onPasswordChange}
            mutationType={mutationType}
          />
        </Col>
      </Row>
      <Row gutter={16} justify="center" align="middle">
        <Col span={24}>
          <ChangePasswordOnLogin />
        </Col>
      </Row>
      <div className="admin_form_buttons">
        <Button
          onClick={() => {
            onCancel();
          }}
          danger
        >
          {t("close")}
        </Button>
        <Button
          type={"primary"}
          htmlType="submit"
          loading={createUserRoleLoading || updateUserRoleLoading}
        >
          {mutationType === "edit" ? t("edit") : t("save")}
        </Button>
      </div>
    </Form>
  );
};

export default RoleForm;
